import React, { useState, useEffect } from "react";
import {
  Calendar,
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  Zap,
  BarChart3,
  Star,
  ArrowRight,
  Menu,
  X,
  Smartphone,
  Download,
  Globe,
} from "lucide-react";
import { useTranslation } from "react-i18next";

// Import your images
import webImage1 from "./../Images/webImage1.png";
import Image1 from "./../Images/Image1.png";
import Image2 from "./../Images/Image2.png";
import Image3 from "./../Images/Image3.png";
import Image4 from "./../Images/Image4.png";

const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  // Check if user has visited before and show language modal if not
  useEffect(() => {
    try {
      const hasVisited = localStorage.getItem("hasVisited");
      const savedLang = localStorage.getItem("lang");

      if (!hasVisited) {
        setShowLanguageModal(true);
      } else if (savedLang) {
        // Parse the JSON string to get the actual value
        const parsedLang = JSON.parse(savedLang);
        i18n.changeLanguage(parsedLang);
      }
    } catch (error) {
      console.error("Error loading language from localStorage:", error);
      // If there's an error, clear localStorage and start fresh
      localStorage.removeItem("lang");
      localStorage.removeItem("hasVisited");
      setShowLanguageModal(true);
    }
  }, [i18n]);

  // Handle language selection from modal (first time visit)
  const handleLanguageSelect = (lang) => {
    try {
      i18n.changeLanguage(lang);
      // Store as JSON string: "ar" instead of ar
      localStorage.setItem("lang", JSON.stringify(lang));
      localStorage.setItem("hasVisited", "true");
      setShowLanguageModal(false);
    } catch (error) {
      console.error("Error saving language:", error);
    }
  };

  // Handle language change from dropdown (regular use)
  const changeLanguage = (lang) => {
    try {
      i18n.changeLanguage(lang);
      // Store as JSON string: "ar" instead of ar
      localStorage.setItem("lang", JSON.stringify(lang));
      setShowLanguageDropdown(false);
    } catch (error) {
      console.error("Error changing language:", error);
    }
  };

  // Scroll to app download section
  const scrollToAppSection = () => {
    const appSection = document.getElementById("app");
    if (appSection) {
      appSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: <Calendar className="w-8 h-8" />,
      title: t("SmartScheduling"),
      description: t("SmartSchedulingDesc"),
      stat: t("SmartSchedulingStat"),
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: t("ClientManagement"),
      description: t("ClientManagementDesc"),
      stat: t("ClientManagementStat"),
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: t("AnalyticsInsights"),
      description: t("AnalyticsInsightsDesc"),
      stat: t("AnalyticsInsightsStat"),
    },
  ];

  const stats = [
    { number: "10K+", label: t("ActiveUsers") },
    { number: "1M+", label: t("AppointmentsBooked") },
    { number: "99.9%", label: t("Uptime") },
    { number: "4.9/5", label: t("UserRating") },
  ];

  const benefits = [
    {
      icon: <Clock className="w-6 h-6" />,
      title: t("SaveHoursDaily"),
      description: t("SaveHoursDesc"),
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: t("NeverMissAppointment"),
      description: t("NeverMissDesc"),
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: t("InstantSetup"),
      description: t("InstantSetupDesc"),
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: t("BoostRevenue"),
      description: t("BoostRevenueDesc"),
    },
  ];

  const languages = [
    { code: "sv", name: "Svenska", flag: "🇸🇪" },
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "ar", name: "العربية", flag: "🇸🇦" },
    // { code: "he", name: "עברית", flag: "🇮🇱" },
  ];

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];

  // App store URLs
  const GOOGLE_PLAY_URL =
    "https://play.google.com/store/apps/details?id=com.mohammad1998a.NeholTorem";
  const APP_STORE_URL = "https://apps.apple.com/us/app/neholtorem/id6755939980";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white overflow-hidden">
      {/* Language Selection Modal (First Time Visit) */}
      {showLanguageModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-slate-900 rounded-3xl border border-slate-700 max-w-md w-full p-8 shadow-2xl animate-slide-in">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-display font-bold mb-2">
                Welcome to Bokahit! 👋
              </h2>
              <p className="text-slate-400">
                Please select your preferred language
              </p>
            </div>

            <div className="space-y-3">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageSelect(lang.code)}
                  className="w-full p-4 bg-slate-800/50 border border-slate-700 rounded-xl hover:border-blue-500/50 hover:bg-slate-800 transition flex items-center justify-between group"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{lang.flag}</span>
                    <span className="text-lg font-semibold">{lang.name}</span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-blue-400 group-hover:translate-x-1 transition" />
                </button>
              ))}
            </div>

            <p className="text-xs text-slate-500 text-center mt-6">
              You can change this later from the menu
            </p>
          </div>
        </div>
      )}

      {/* Animated Background Elements */}
      <div className="fixed inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@400;500;700&display=swap');
        
        .main-page * {
          font-family: 'Droid Arabic Kufi', 'DM Sans', sans-serif;
        }
        
        .main-page h1, 
        .main-page h2, 
        .main-page h3, 
        .main-page .font-display {
          font-family: 'Droid Arabic Kufi', 'Syne', sans-serif;
        }

        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(20px, -50px) scale(1.1); }
          50% { transform: translate(-20px, 20px) scale(0.9); }
          75% { transform: translate(50px, 10px) scale(1.05); }
        }

        .animate-blob {
          animation: blob 7s infinite;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slide-in {
          animation: slideInUp 0.6s ease-out forwards;
        }

        .gradient-text {
          background: linear-gradient(135deg, #60a5fa 0%, #a78bfa 50%, #ec4899 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .card-hover {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .card-hover:hover {
          transform: translateY(-8px) scale(1.02);
        }

        .glow-button {
          position: relative;
          overflow: hidden;
        }

        .glow-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          transition: left 0.5s;
        }

        .glow-button:hover::before {
          left: 100%;
        }

        @keyframes phoneFloat {
          0%, 100% { transform: translateY(0px) rotate(-2deg); }
          50% { transform: translateY(-30px) rotate(2deg); }
        }

        .animate-phone-float {
          animation: phoneFloat 4s ease-in-out infinite;
        }
      `}</style>

      <div className="main-page">
        {/* Navigation */}
        <nav
          className={`fixed w-full z-50 transition-all duration-300 ${
            scrolled
              ? "bg-slate-950/80 backdrop-blur-lg shadow-xl"
              : "bg-transparent"
          }`}
        >
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-display font-bold">Bokahit</span>
              </div>

              <div className="hidden md:flex items-center space-x-8">
                <a href="#features" className="hover:text-blue-400 transition">
                  {t("Features")}
                </a>
                <a href="#app" className="hover:text-blue-400 transition">
                  {t("MobileApp")}
                </a>
                <a href="#benefits" className="hover:text-blue-400 transition">
                  {t("Benefits")}
                </a>
                <a href="#contact" className="hover:text-blue-400 transition">
                  {t("Contact") || "Contact"}
                </a>

                {/* Language Selector */}
                <div className="relative">
                  <button
                    onClick={() =>
                      setShowLanguageDropdown(!showLanguageDropdown)
                    }
                    className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg hover:border-blue-500/50 transition"
                  >
                    <Globe className="w-4 h-4" />
                    <span className="text-xl">{currentLanguage.flag}</span>
                    <span className="text-sm font-medium">
                      {currentLanguage.name}
                    </span>
                  </button>

                  {showLanguageDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden z-50">
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => changeLanguage(lang.code)}
                          className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-800 transition ${
                            i18n.language === lang.code
                              ? "bg-slate-800 border-l-2 border-blue-500"
                              : ""
                          }`}
                        >
                          <span className="text-2xl">{lang.flag}</span>
                          <span className="text-sm font-medium">
                            {lang.name}
                          </span>
                          {i18n.language === lang.code && (
                            <CheckCircle className="w-4 h-4 ml-auto text-blue-400" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="hidden md:flex items-center gap-3">
                  <a
                    href="https://www.instagram.com/neholtorem/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 bg-slate-800/50 border border-slate-700 rounded-lg hover:border-pink-500/50 hover:bg-pink-500/10 transition flex items-center justify-center group"
                    aria-label="Instagram"
                  >
                    <svg
                      className="w-5 h-5 text-slate-400 group-hover:text-pink-400 transition"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  </a>

                  <a
                    href="https://www.facebook.com/profile.php?id=61583627140017"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 bg-slate-800/50 border border-slate-700 rounded-lg hover:border-blue-500/50 hover:bg-blue-500/10 transition flex items-center justify-center group"
                    aria-label="Facebook"
                  >
                    <svg
                      className="w-5 h-5 text-slate-400 group-hover:text-blue-400 transition"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </a>
                </div>
                <button
                  onClick={scrollToAppSection}
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-500/50 transition glow-button"
                >
                  {t("GetStarted")}
                </button>
              </div>

              <button
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
              <div className="md:hidden mt-4 pb-4 space-y-3 animate-slide-in">
                <a
                  href="#features"
                  className="block hover:text-blue-400 transition"
                >
                  {t("Features")}
                </a>
                <a href="#app" className="block hover:text-blue-400 transition">
                  {t("MobileApp")}
                </a>
                <a
                  href="#benefits"
                  className="block hover:text-blue-400 transition"
                >
                  {t("Benefits")}
                </a>
                <a
                  href="#contact"
                  className="block hover:text-blue-400 transition"
                >
                  {t("Contact") || "Contact"}
                </a>

                {/* Mobile Language Selector */}
                <div className="pt-3 border-t border-slate-800">
                  <p className="text-xs text-slate-400 mb-2 uppercase tracking-wider">
                    Language
                  </p>
                  <div className="space-y-2">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full px-4 py-2 flex items-center gap-3 rounded-lg transition ${
                          i18n.language === lang.code
                            ? "bg-blue-500/20 border border-blue-500/50"
                            : "bg-slate-800/50 border border-slate-700 hover:border-blue-500/30"
                        }`}
                      >
                        <span className="text-2xl">{lang.flag}</span>
                        <span className="text-sm font-medium">{lang.name}</span>
                        {i18n.language === lang.code && (
                          <CheckCircle className="w-4 h-4 ml-auto text-blue-400" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => {
                    scrollToAppSection();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full px-6 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg font-semibold"
                >
                  {t("GetStarted")}
                </button>
              </div>
            )}
          </div>
        </nav>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-4xl mx-auto animate-slide-in">
              <div className="inline-block mb-6 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full">
                <span className="text-blue-400 text-sm font-semibold">
                  {t("TrustedByProfessionals")}
                </span>
              </div>

              <h1 className="text-5xl md:text-7xl font-display font-bold mb-6 leading-tight">
                {t("AppointmentManagement")}{" "}
                <span className="gradient-text">{t("Simplified")}</span>
              </h1>

              <p className="text-xl md:text-2xl text-slate-300 mb-10 leading-relaxed">
                {t("HeroDescription")}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={scrollToAppSection}
                  className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg font-semibold text-lg hover:shadow-2xl hover:shadow-blue-500/50 transition transform hover:scale-105 glow-button flex items-center gap-2"
                >
                  {t("StartFreeTrial")}
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg font-semibold text-lg hover:bg-white/20 transition">
                  {t("WatchDemo")}
                </button>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
                {stats.map((stat, idx) => (
                  <div
                    key={idx}
                    className="text-center animate-slide-in"
                    style={{ animationDelay: `${idx * 0.1}s` }}
                  >
                    <div className="text-3xl md:text-4xl font-bold font-display gradient-text mb-2">
                      {stat.number}
                    </div>
                    <div className="text-slate-400 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        {/* Features Section */}
        <section id="features" className="relative py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
                {t("EverythingYouNeedTo")}{" "}
                <span className="gradient-text">{t("Succeed")}</span>
              </h2>
              <p className="text-xl text-slate-300 max-w-2xl mx-auto">
                {t("BuiltForProfessionals")}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {features.map((feature, idx) => (
                <div
                  key={idx}
                  className={`relative p-8 rounded-2xl border transition-all duration-500 card-hover ${
                    activeFeature === idx
                      ? "bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-blue-500/50 shadow-2xl shadow-blue-500/20"
                      : "bg-slate-900/50 border-slate-800 hover:border-slate-700"
                  }`}
                  onMouseEnter={() => setActiveFeature(idx)}
                >
                  <div
                    className={`w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-all duration-500 ${
                      activeFeature === idx
                        ? "bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg shadow-blue-500/50"
                        : "bg-slate-800"
                    }`}
                  >
                    {feature.icon}
                  </div>

                  <h3 className="text-2xl font-display font-bold mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-300 mb-4 leading-relaxed">
                    {feature.description}
                  </p>

                  <div className="inline-block px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full">
                    <span className="text-blue-400 font-semibold">
                      {feature.stat}
                    </span>
                  </div>

                  {activeFeature === idx && (
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 opacity-20 blur-2xl rounded-full"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* Mobile App Showcase Section */}
        <section
          id="app"
          className="relative py-20 px-6 bg-gradient-to-b from-slate-900/30 to-slate-900/50 scroll-mt-20"
        >
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block mb-4 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full">
                <span className="text-purple-400 text-sm font-semibold flex items-center gap-2">
                  <Smartphone className="w-4 h-4" />
                  {t("AvailableOnMobile")}
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
                {t("ManageEverything")}{" "}
                <span className="gradient-text">{t("OnTheGo")}</span>
              </h2>
              <p className="text-xl text-slate-300 max-w-2xl mx-auto">
                {t("MobileAppDescription")}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* App Screenshots/Images */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-600 rounded-3xl blur-3xl opacity-20"></div>

                {/* Main Phone Mockup */}
                <div className="relative z-10 animate-phone-float">
                  <div className="mx-auto w-64 h-[520px] bg-slate-900 rounded-[3rem] border-8 border-slate-800 shadow-2xl overflow-hidden">
                    <img
                      src={webImage1}
                      alt="NeholTorem App"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Floating Feature Cards */}
                <div className="absolute top-10 -left-4 bg-slate-900/80 backdrop-blur-xl rounded-xl p-3 border border-slate-700 shadow-xl hidden lg:block animate-float">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">
                        {t("BookYourAppointment")}
                      </p>
                      <p className="text-sm font-bold">{t("ConfirmBooking")}</p>
                    </div>
                  </div>
                </div>

                <div
                  className="absolute bottom-10 -right-4 bg-slate-900/80 backdrop-blur-xl rounded-xl p-3 border border-slate-700 shadow-xl hidden lg:block animate-float"
                  style={{ animationDelay: "1s" }}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">
                        {t("ClientManagement")}
                      </p>
                      <p className="text-sm font-bold">{t("ActiveClients")}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* App Features & Download Buttons */}
              <div>
                <h3 className="text-3xl font-display font-bold mb-6">
                  {t("YourBusinessInYourPocket")}
                </h3>
                <p className="text-lg text-slate-300 mb-8">
                  {t("MobileAppFullDescription")}
                </p>

                <div className="space-y-4 mb-10">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h4 className="font-bold mb-1">{t("RealTimeSync")}</h4>
                      <p className="text-slate-400 text-sm">
                        {t("RealTimeSyncDesc")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h4 className="font-bold mb-1">
                        {t("PushNotifications")}
                      </h4>
                      <p className="text-slate-400 text-sm">
                        {t("PushNotificationsDesc")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center justify-center flex-shrink-0">
                      <Smartphone className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <h4 className="font-bold mb-1">{t("WorksOffline")}</h4>
                      <p className="text-slate-400 text-sm">
                        {t("WorksOfflineDesc")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Download Buttons */}
                <div className="space-y-4">
                  <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
                    {t("DownloadNow")}
                  </p>

                  {/* Google Play Button */}
                  <a
                    href={GOOGLE_PLAY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 bg-slate-900/50 border border-slate-700 rounded-xl hover:border-blue-500/50 hover:bg-slate-900/80 transition card-hover group"
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition">
                      <svg
                        className="w-7 h-7 text-white"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-slate-400">
                        {t("GetItOnGooglePlay")}
                      </p>
                      <p className="text-lg font-bold">{t("GooglePlay")}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-blue-400 group-hover:translate-x-1 transition" />
                  </a>

                  {/* Apple App Store Button */}
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 bg-slate-900/50 border border-slate-700 rounded-xl hover:border-blue-500/50 hover:bg-slate-900/80 transition card-hover group"
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition">
                      <svg
                        className="w-7 h-7 text-white"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-slate-400">
                        {t("DownloadOnTheAppStore")}
                      </p>
                      <p className="text-lg font-bold">{t("AppStore")}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-blue-400 group-hover:translate-x-1 transition" />
                  </a>
                </div>

                <p className="text-xs text-slate-500 mt-6">
                  {t("FreeToDownload")}
                </p>
              </div>
            </div>

            {/* Additional App Screenshots Gallery */}
            <div className="mt-20">
              <h3 className="text-2xl font-display font-bold text-center mb-10">
                {t("SeeItInAction")}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[Image1, Image2, Image3, Image4].map((image, idx) => (
                  <div
                    key={idx}
                    className="aspect-[9/16] bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden card-hover"
                  >
                    <img
                      src={image}
                      alt={`NeholTorem App Screenshot ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        {/* Benefits Section */}
        <section id="benefits" className="relative py-20 px-6 bg-slate-900/30">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">
                  {t("WhyProfessionalsChoose")}{" "}
                  <span className="gradient-text">{t("NeholTorem")}</span>
                </h2>
                <p className="text-xl text-slate-300 mb-8">
                  {t("BenefitsDescription")}
                </p>

                <div className="space-y-6">
                  {benefits.map((benefit, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-4 p-4 rounded-xl bg-slate-900/50 border border-slate-800 hover:border-blue-500/30 transition card-hover"
                    >
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                        {benefit.icon}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold mb-1">
                          {benefit.title}
                        </h3>
                        <p className="text-slate-400">{benefit.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl blur-3xl opacity-20"></div>
                <div className="relative p-8 bg-slate-900/80 backdrop-blur-xl rounded-3xl border border-slate-800 animate-float">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl">
                      <span className="text-slate-300">
                        {t("TodaysAppointments")}
                      </span>
                      <span className="text-2xl font-bold text-green-400">
                        24
                      </span>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl">
                      <span className="text-slate-300">
                        {t("ClientSatisfaction")}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-yellow-400">
                          4.9
                        </span>
                        <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                      </div>
                    </div>

                    <div className="p-4 bg-slate-800/50 rounded-xl">
                      <span className="text-slate-300 block mb-3">
                        {t("ThisMonthRevenue")}
                      </span>
                      <div className="text-3xl font-bold gradient-text mb-2">
                        $47,250
                      </div>
                      <div className="flex items-center gap-2 text-green-400 text-sm">
                        <TrendingUp className="w-4 h-4" />
                        <span>{t("PercentageFromLastMonth")}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-800/50 rounded-xl text-center">
                        <div className="text-2xl font-bold text-blue-400 mb-1">
                          156
                        </div>
                        <div className="text-slate-400 text-sm">
                          {t("ActiveClients")}
                        </div>
                      </div>
                      <div className="p-4 bg-slate-800/50 rounded-xl text-center">
                        <div className="text-2xl font-bold text-purple-400 mb-1">
                          98%
                        </div>
                        <div className="text-slate-400 text-sm">
                          {t("ShowRate")}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* CTA Section */}
        <section className="relative py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="relative p-12 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-3xl border border-blue-500/20 backdrop-blur-xl">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl blur-3xl opacity-10"></div>

              <div className="relative">
                <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">
                  {t("ReadyToTransform")}
                </h2>
                <p className="text-xl text-slate-300 mb-8">
                  {t("CTADescription")}
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={scrollToAppSection}
                    className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg font-semibold text-lg hover:shadow-2xl hover:shadow-blue-500/50 transition transform hover:scale-105 glow-button flex items-center justify-center gap-2"
                  >
                    {t("StartFreeTrial")}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  <button
                    onClick={scrollToAppSection}
                    className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg font-semibold text-lg hover:bg-white/20 transition flex items-center justify-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    {t("DownloadApp")}
                  </button>
                </div>

                <p className="text-slate-400 text-sm mt-6">{t("FreeTrial")}</p>
              </div>
            </div>
          </div>
        </section>
        {/* Contact Us Section */}
        <section id="contact" className="relative py-20 px-6 bg-slate-900/30">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
                {t("GetInTouch") || "Get In Touch"}
              </h2>
              <p className="text-xl text-slate-300">
                {t("ContactDescription") ||
                  "Have questions? We're here to help!"}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* WhatsApp */}
              <a
                href="https://wa.me/46790205074"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-4 p-6 bg-slate-900/50 border border-slate-700 rounded-xl hover:border-green-500/50 hover:bg-slate-900/80 transition card-hover group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition">
                  <svg
                    className="w-8 h-8 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                </div>
                <div className="text-center">
                  <h3 className="font-bold text-lg mb-1">
                    {t("WhatsApp") || "WhatsApp"}
                  </h3>
                  <p className="text-slate-400 text-sm mb-2">
                    {t("ChatWithUs") || "Chat with us"}
                  </p>
                  <p className="text-green-400 font-mono text-sm">
                    +972 52-747-7128
                  </p>
                </div>
              </a>

              {/* Phone Call */}
              <a
                href="tel:+46790205074"
                className="flex flex-col items-center gap-4 p-6 bg-slate-900/50 border border-slate-700 rounded-xl hover:border-blue-500/50 hover:bg-slate-900/80 transition card-hover group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition">
                  <svg
                    className="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </div>
                <div className="text-center">
                  <h3 className="font-bold text-lg mb-1">
                    {t("Phone") || "Phone"}
                  </h3>
                  <p className="text-slate-400 text-sm mb-2">
                    {t("CallUs") || "Call us directly"}
                  </p>
                  <p className="text-blue-400 font-mono text-sm">
                    +972 52-747-7128
                  </p>
                </div>
              </a>

              {/* SMS */}
              <a
                href="sms:+46790205074"
                className="flex flex-col items-center gap-4 p-6 bg-slate-900/50 border border-slate-700 rounded-xl hover:border-purple-500/50 hover:bg-slate-900/80 transition card-hover group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition">
                  <svg
                    className="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                    />
                  </svg>
                </div>
                <div className="text-center">
                  <h3 className="font-bold text-lg mb-1">
                    {t("SMS") || "SMS"}
                  </h3>
                  <p className="text-slate-400 text-sm mb-2">
                    {t("TextUs") || "Send us a text"}
                  </p>
                  <p className="text-purple-400 font-mono text-sm">
                    +972 52-747-7128
                  </p>
                </div>
              </a>
            </div>

            <div className="mt-12 text-center">
              <p className="text-slate-400">
                {t("ContactAvailability") || "Available 24/7 to assist you"}
              </p>
            </div>
          </div>
        </section>
        {/* Footer */}
        <footer className="relative py-12 px-6 border-t border-slate-800">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              {/* Brand */}
              <div className="flex flex-col items-center md:items-start">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-xl font-display font-bold">
                    Bokahit
                  </span>
                </div>
                <p className="text-slate-400 text-sm max-w-xs text-center md:text-left">
                  {t("FooterDescription") ||
                    "Simplifying appointment management for professionals worldwide."}
                </p>
              </div>

              {/* Quick Links */}
              <div className="flex flex-col items-center">
                <h3 className="font-bold mb-4">
                  {t("QuickLinks") || "Quick Links"}
                </h3>
                <div className="flex flex-col gap-3 text-slate-400">
                  <a href="#features" className="hover:text-white transition">
                    {t("Features")}
                  </a>
                  <a href="#app" className="hover:text-white transition">
                    {t("MobileApp")}
                  </a>
                  <a href="#benefits" className="hover:text-white transition">
                    {t("Benefits")}
                  </a>
                  <a href="#contact" className="hover:text-white transition">
                    {t("Contact")}
                  </a>
                </div>
              </div>

              {/* Social Media */}
              <div className="flex flex-col items-center md:items-end">
                <h3 className="font-bold mb-4">
                  {t("FollowUs") || "Follow Us"}
                </h3>
                <div className="flex gap-3">
                  <a
                    href="https://www.instagram.com/neholtorem/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-slate-800/50 border border-slate-700 rounded-lg hover:border-pink-500/50 hover:bg-pink-500/10 transition flex items-center justify-center group"
                    aria-label="Instagram"
                  >
                    <svg
                      className="w-6 h-6 text-slate-400 group-hover:text-pink-400 transition"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  </a>

                  <a
                    href="https://www.facebook.com/profile.php?id=61583627140017"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-slate-800/50 border border-slate-700 rounded-lg hover:border-blue-500/50 hover:bg-blue-500/10 transition flex items-center justify-center group"
                    aria-label="Facebook"
                  >
                    <svg
                      className="w-6 h-6 text-slate-400 group-hover:text-blue-400 transition"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-slate-400 text-sm">{t("CopyrightMainPage")}</p>

              <div className="flex gap-6 text-slate-400 text-sm">
                <a href="#" className="hover:text-white transition">
                  {t("Privacy")}
                </a>
                <a href="#" className="hover:text-white transition">
                  {t("Terms")}
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;
